import {
  type InternalCalibrationArea,
  type InternalLabel,
  type InternalLabelArea,
  LabelInspectionAreaType,
} from '@ulysses-inc/harami_api_client'
import { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useQueryLabelNames } from 'src/features/labelMaster/api/useQueryLabelNames'
import { useSaveLabel } from 'src/features/labelMaster/api/useSaveLabel'
import { usePreventPageOut } from '../../../edit/usePreventPageOut'
import { clampRect } from '../../normalizedRect'

type Input = {
  label: InternalLabel
  labelMasterId: string
  onClose: () => void
}

export const useLabelForm = ({ label, labelMasterId, onClose }: Input) => {
  const form = useForm<LabelFormValues>({
    defaultValues: {
      labelName: label.name,
      imageId: label.labelImageId,
      imageUrl: label.labelUrl,
      areas: label.labelAreas,
      deletedAreaIds: [],
      calibrationAreas: label.calibrationAreas,
    },
  })

  // --- UI state ---

  const [editingTarget, setEditingTarget] = useState<EditingTarget>({
    type: 'none',
  })

  // --- Actions ---

  const saveArea = useCallback(
    (area: InternalLabelArea) => {
      form.setValue('areas', upsert(form.getValues('areas'), area), {
        shouldDirty: true,
      })
    },
    [form],
  )

  const deleteArea = useCallback(
    (id: string) => {
      form.setValue(
        'areas',
        form.getValues('areas').filter(a => a.id !== id),
        { shouldDirty: true },
      )
      form.setValue(
        'deletedAreaIds',
        [...form.getValues('deletedAreaIds'), id],
        { shouldDirty: true },
      )
    },
    [form],
  )

  const saveCalibrationArea = useCallback(
    (ca: InternalCalibrationArea) => {
      form.setValue(
        'calibrationAreas',
        upsert(form.getValues('calibrationAreas'), ca),
        { shouldDirty: true },
      )
    },
    [form],
  )

  const deleteCalibrationArea = useCallback(
    (id: string) => {
      form.setValue(
        'calibrationAreas',
        form.getValues('calibrationAreas').filter(a => a.id !== id),
        { shouldDirty: true },
      )
    },
    [form],
  )

  const handleAreaSaved = useCallback(
    (area: InternalLabelArea) => {
      saveArea(area)
      setEditingTarget({ type: 'none' })
    },
    [saveArea],
  )

  const cancelEditing = useCallback(() => {
    setEditingTarget({ type: 'none' })
  }, [])

  // --- Reactive values ---

  // watch() は React Compiler と非互換のため、useWatch を使用する。
  // useWatch の戻り型は DeepPartialSkipArrayKey<T>（全フィールドが optional）だが、
  // defaultValues を必ず渡しているためランタイムでは常に完全な値が保証される。
  const formValues = useWatch({ control: form.control }) as LabelFormValues

  // --- Validation ---

  const errors = useMemo(
    () => ({
      areas:
        formValues.areas.length > MAX_AREAS_COUNT
          ? [`検査範囲は${MAX_AREAS_COUNT}個まで登録できます`]
          : [],
      calibrationAreas:
        formValues.calibrationAreas.length > MAX_CALIBRATION_AREAS_COUNT
          ? [`位置補正基準は${MAX_CALIBRATION_AREAS_COUNT}個まで登録できます`]
          : [],
    }),
    [formValues.areas.length, formValues.calibrationAreas.length],
  )

  // --- Save ---

  const { showInfoSnackbar } = useSnackbar()
  const { mutateAsync } = useSaveLabel(labelMasterId)

  const handleSave = useCallback(() => {
    if (errors.areas.length > 0 || errors.calibrationAreas.length > 0) return

    // 位置補正基準の追加が必要な場合は、保存せずに位置補正基準の編集モードに遷移する
    if (
      needsCalibrationArea(
        form.getValues('areas'),
        form.getValues('calibrationAreas'),
      )
    ) {
      showInfoSnackbar('位置補正基準を指定してください。')
      setEditingTarget({ type: 'calibration-new' })
      return
    }

    form.handleSubmit(async formValues => {
      try {
        await mutateAsync({
          id: label.id,
          name: formValues.labelName || undefined,
          labelImageId: formValues.imageId,
          labelMasterId,
          saveAreas: formValues.areas.map(clampRect),
          deleteAreas: formValues.deletedAreaIds,
          calibrationAreas: formValues.calibrationAreas.map(clampRect),
        })
        onClose()
      } catch {
        //
      }
    })()
  }, [
    errors,
    showInfoSnackbar,
    form,
    mutateAsync,
    label.id,
    labelMasterId,
    onClose,
  ])

  // --- Close guard ---

  // 編集内容が保存されていない状態でページを離れようとしたときに確認する
  const { handleBeforeUnload: handleClose } = usePreventPageOut({
    isModified: form.formState.isDirty,
    onLeavePage: onClose,
  })

  // --- Data queries ---

  const { data: nameHistories = [] } = useQueryLabelNames()

  return {
    control: form.control,
    formValues,
    setValue: form.setValue,
    errors,
    editingTarget,
    canSubmit: editingTarget.type === 'none',
    setEditingTarget,
    handleAreaSaved,
    deleteArea,
    saveCalibrationArea,
    deleteCalibrationArea,
    cancelEditing,
    handleSave,
    handleClose,
    nameHistories,
  }
}

export type LabelFormValues = {
  labelName: string
  imageId: string
  imageUrl: string
  areas: InternalLabelArea[]
  deletedAreaIds: string[]
  calibrationAreas: InternalCalibrationArea[]
}

export type EditingTarget =
  | { type: 'area-new' }
  | { type: 'area-edit'; area: InternalLabelArea }
  | { type: 'calibration-new' }
  | { type: 'calibration-edit'; calibrationArea: InternalCalibrationArea }
  | { type: 'none' }

const MAX_AREAS_COUNT = 10
const MAX_CALIBRATION_AREAS_COUNT = 2

// 位置補正基準の追加が必要かどうかを判定するロジック
const needsCalibrationArea = (
  areas: InternalLabelArea[],
  calibrationAreas: { id: string }[],
): boolean => {
  if (areas.length === 0) return false
  // パターンマッチングの検査範囲がある場合は、位置補正基準は不要。
  if (areas.some(a => a.type === LabelInspectionAreaType.PATTERN)) return false
  if (calibrationAreas.length > 0) return false
  return true
}

const upsert = <T extends { id: string }>(list: T[], item: T): T[] => {
  const idx = list.findIndex(a => a.id === item.id)
  if (idx === -1) return [...list, item]
  return list.map((a, i) => (i === idx ? item : a))
}
